import { useEffect, useState } from 'react';
import { getChatSocket } from '@services/realtime.socket';
import { useAuthStore } from '@stores/auth.store';

/**
 * Listen for typing:start / typing:stop (server → client) in the chat room.
 *
 * Backend broadcasts:
 *   typing:start  → { userId, userEmail, chatId }
 *   typing:stop   → { userId, chatId }
 *
 * Auto-clear: backend has a 5s auto-stop, but we add a 6s client-side
 * fallback in case the stop event is lost.
 */
const TYPING_CLIENT_TIMEOUT_MS = 6000;

export function useChatTyping(chatId) {
    const [typingUsers, setTypingUsers] = useState([]);
    const currentUserId = useAuthStore((s) => s.user?.id ?? s.user?.userId ?? null);

    useEffect(() => {
        if (!chatId) {
            setTypingUsers([]);
            return () => {};
        }
        const s = getChatSocket();
        if (!s) return () => {};

        const chatKey = String(chatId);
        const meKey = currentUserId != null ? String(currentUserId) : null;
        const timers = new Map();

        const clearUserTimer = (userId) => {
            const t = timers.get(userId);
            if (t) {
                clearTimeout(t);
                timers.delete(userId);
            }
        };

        const removeUser = (userId) => {
            clearUserTimer(userId);
            setTypingUsers((prev) => prev.filter((u) => u.userId !== userId));
        };

        const onTypingStart = (payload) => {
            if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
                // eslint-disable-next-line no-console
                console.debug('[chat] typing:start recv', payload);
            }
            const targetChatId = payload?.chatId;
            if (targetChatId != null && String(targetChatId) !== chatKey) return;
            const userIdRaw = payload?.userId;
            const userId = userIdRaw != null ? String(userIdRaw) : '';
            if (!userId || (meKey && userId === meKey)) return;
            const userEmail = payload?.userEmail ?? '';
            const userName = payload?.userName ?? userEmail?.split('@')[0] ?? 'Someone';

            setTypingUsers((prev) => {
                if (prev.some((u) => String(u.userId) === userId)) return prev;
                return [...prev, { userId, userName }];
            });

            clearUserTimer(userId);
            timers.set(
                userId,
                setTimeout(() => removeUser(userId), TYPING_CLIENT_TIMEOUT_MS),
            );
        };

        const onTypingStop = (payload) => {
            if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
                // eslint-disable-next-line no-console
                console.debug('[chat] typing:stop recv', payload);
            }
            const targetChatId = payload?.chatId;
            if (targetChatId != null && String(targetChatId) !== chatKey) return;
            const userIdRaw = payload?.userId;
            const userId = userIdRaw != null ? String(userIdRaw) : '';
            if (userId) removeUser(userId);
        };

        s.on('typing:start', onTypingStart);
        s.on('typing:stop', onTypingStop);

        return () => {
            s.off('typing:start', onTypingStart);
            s.off('typing:stop', onTypingStop);
            for (const t of timers.values()) clearTimeout(t);
            timers.clear();
            setTypingUsers([]);
        };
    }, [chatId, currentUserId]);

    return { typingUsers };
}
